import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, ApiError, Button, Eyebrow, Field, Input, Spinner } from '@busbook/ui';
import { useFeature, useTenant } from '../lib/tenant.js';
import { useCustomer } from '../lib/customer-session.js';
import { useTravellers } from '../lib/travellers.js';
import { api } from '../lib/api.js';
import { checkoutRedirectTarget } from '../lib/checkoutRedirect.js';
import { SandboxInstruments } from '../components/SandboxInstruments.js';
import { useT } from '../i18n/index.js';
import { PaymentStatus } from '../components/PaymentStatus.js';

/**
 * Checkout (docs/05 §3–4). Contact email is mandatory: it is the notification
 * address and the key a guest uses to retrieve the booking later.
 *
 * Payment is deliberately not confirmed here — the booking flips to CONFIRMED
 * only when the provider's webhook arrives, so this screen creates the booking
 * and then polls for that confirmation.
 */

interface BookingView {
  reference: string;
  status: string;
  totalCents: number;
  currency: string;
}

export function Checkout() {
  const t = useT();
  // Only fetched when there is both an account to fetch for and an operator offering the
  // feature — a guest has nothing saved, and a request that always 404s is noise.
  const { user } = useCustomer();
  const travellersEnabled = useFeature('travellers');
  const { travellers: savedTravellers } = useTravellers(Boolean(user) && travellersEnabled);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { settings, branding } = useTenant();

  const tripId = params.get('trip') ?? '';
  const fromStopId = params.get('from') ?? '';
  const toStopId = params.get('to') ?? '';
  const seatCodes = (params.get('seats') ?? '').split(',').filter(Boolean);

  const [names, setNames] = useState<Record<string, string>>(() =>
    Object.fromEntries(seatCodes.map((code) => [code, ''])),
  );
  const [contact, setContact] = useState({ name: '', email: '', phone: '' });
  const [promotionCode, setPromotionCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<'form' | 'paying' | 'handover' | 'waiting'>('form');
  /** The validated hosted-checkout URL, kept so the handover screen can offer it as a link. */
  const [handoverUrl, setHandoverUrl] = useState<string | null>(null);
  const [booking, setBooking] = useState<BookingView | null>(null);

  const hasSeats = seatCodes.length > 0;
  useEffect(() => {
    // Reaching checkout without seats means a stale or hand-edited link.
    if (!hasSeats) navigate('/', { replace: true });
  }, [hasSeats, navigate]);

  /**
   * Polls until the webhook has confirmed the booking. The UI never decides that
   * a payment succeeded — it waits for the server to say so.
   */
  useEffect(() => {
    if (stage !== 'waiting' || !booking) return;
    let cancelled = false;

    const poll = setInterval(async () => {
      try {
        const fresh = await api.get<BookingView>(`/public/bookings/${booking.reference}`);
        if (cancelled) return;
        if (fresh.status === 'CONFIRMED') {
          clearInterval(poll);
          navigate(`/booking/${booking.reference}`, { replace: true });
        } else if (fresh.status === 'EXPIRED') {
          clearInterval(poll);
          setStage('form');
          setError('Your payment window closed and the seats were released. Please start again.');
        }
      } catch {
        // Transient failures are fine; the next tick retries.
      }
    }, 2000);

    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, [stage, booking, navigate]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setStage('paying');

    try {
      const result = await api.request<{
        booking: BookingView;
        payment: {
          providerRef: string;
          clientSecret: string;
          publishableKey: string;
          checkoutUrl?: string;
        };
      }>('/public/bookings', {
        method: 'POST',
        // Signed in, and the server insists on it (DECISIONS D68): POST /public/bookings
        // refuses anything that is not a CUSTOMER of this tenant. Suppressing the bearer
        // token here made every checkout arrive anonymous, so the passenger was told to
        // sign in by the very request they had just signed in to make.
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          tripId,
          fromStopId,
          toStopId,
          seats: seatCodes.map((code) => ({
            seatCode: code,
            passengerName: names[code]?.trim() || contact.name.trim(),
          })),
          contact: {
            name: contact.name.trim(),
            email: contact.email.trim(),
            ...(contact.phone.trim() ? { phone: contact.phone.trim() } : {}),
          },
          ...(promotionCode.trim() ? { promotionCode: promotionCode.trim() } : {}),
        }),
      });

      setBooking(result.booking);

      // Nothing to pay: a fully discounted booking is already confirmed, so there is no
      // provider to hand over to and no webhook to wait for. Straight to the ticket.
      if (!result.payment) {
        navigate(`/booking/${result.booking.reference}`, { replace: true });
        return;
      }

      // A hosted provider takes the passenger to its own page to pay. Their seats
      // are already held and the booking exists, so leaving is safe: whichever way
      // it ends, they come back to the ticket, which polls for the webhook.
      //
      // The URL is validated, never followed blindly — see checkoutRedirect.ts.
      const target = checkoutRedirectTarget(result.payment);
      if (target) {
        setHandoverUrl(target);
        setStage('handover');
        /**
         * Navigate on the NEXT FRAME, not in this tick.
         *
         * Assigning immediately means the browser leaves before React has painted
         * anything, so a passenger who is about to be sent to another company's
         * page sees a frozen form instead of being told where they are going. One
         * frame is enough for the handover screen to render, and costs nothing.
         */
        requestAnimationFrame(() => window.location.assign(target));
        return;
      }

      // A checkoutUrl we refused to follow is a broken integration, not a
      // successful booking — say so rather than sitting on a spinner.
      if (result.payment.checkoutUrl) {
        setError('We could not open the payment page securely. Please contact support.');
        setStage('form');
        return;
      }

      setStage('waiting');
    } catch (err) {
      setStage('form');
      if (err instanceof ApiError) {
        setError(
          err.code === 'HOLD_EXPIRED'
            ? 'Your seat hold expired. Please choose your seats again.'
            : err.code === 'SEAT_TAKEN'
              ? 'One of those seats was just taken. Please choose again.'
              : err.message,
        );
      } else {
        setError('We could not start your booking. Check your connection and try again.');
      }
    }
  }

  /**
   * The handover. Rendered for the frame before the browser leaves, and left on
   * screen if it does not — a blocked or slow navigation must not look like a
   * hung page, so the same URL is always offered as something to press.
   */
  if (stage === 'handover' && handoverUrl) {
    return (
      <section className="grid min-h-[60vh] place-items-center px-4 py-10 text-center">
        <div>
          <Spinner size={28} label={t('k.opening')} />
          <h1 className="mt-4 text-lg font-semibold tracking-tight">{t('k.takingYou')}</h1>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
            {t('k.handoverHint')}
          </p>
          <a
            href={handoverUrl}
            className="mt-5 inline-block text-sm font-medium underline underline-offset-4"
            style={{ color: 'var(--color-primary)' }}
          >
            {t('k.continueToPay')}
          </a>
        </div>
      </section>
    );
  }

  if (stage === 'waiting') {
    return (
      <PaymentStatus
        tone="waiting"
        title={t('k.confirming')}
        detail={t('k.confirmingHint')}
        {...(booking ? { reference: booking.reference } : {})}
      />
    );
  }

  return (
    <section className="px-4 py-6 pb-10">
      <Eyebrow>{t('k.checkout')}</Eyebrow>
      <h1 className="mt-1 text-xl font-semibold tracking-tight">{t('k.whoTravelling')}</h1>

      <form onSubmit={submit} className="mt-5 flex flex-col gap-5">
        <div className="flex flex-col gap-4">
          {seatCodes.map((code) => (
            <Field key={code} label={`Passenger in seat ${code}`} htmlFor={`p-${code}`}>
              <Input
                id={`p-${code}`}
                autoComplete="name"
                placeholder={t('k.fullName')}
                value={names[code] ?? ''}
                onChange={(e) => setNames({ ...names, [code]: e.target.value })}
                required
              />
              {/*
                Saved people, offered as a row of chips under the field they fill.
                Picking one writes the NAME into the field — the booking never references
                the traveller record, so removing somebody later cannot rewrite a ticket
                that has already been scanned.
              */}
              {savedTravellers.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {savedTravellers.map((traveller) => (
                    <button
                      key={traveller.id}
                      type="button"
                      className="trip-action"
                      style={{ minHeight: 30, fontSize: '0.75rem' }}
                      onClick={() => setNames({ ...names, [code]: traveller.fullName })}
                    >
                      {traveller.fullName}
                    </button>
                  ))}
                </div>
              )}
            </Field>
          ))}
        </div>

        <div
          className="rounded-xl border p-4"
          style={{ borderColor: 'var(--color-line)', background: 'var(--color-surface)' }}
        >
          <Eyebrow>{t('k.contact')}</Eyebrow>
          <div className="mt-3 flex flex-col gap-4">
            <Field label={t('c.yourName')} htmlFor="c-name">
              <Input
                id="c-name"
                autoComplete="name"
                value={contact.name}
                onChange={(e) => setContact({ ...contact, name: e.target.value })}
                required
              />
            </Field>
            <Field
              label={t('c.email')}
              htmlFor="c-email"
              hint={t('k.emailHint')}
            >
              <Input
                id="c-email"
                type="email"
                autoComplete="email"
                value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
                required
              />
            </Field>
            <Field label={t('c.phone')} htmlFor="c-phone" hint={t('k.phoneHint')}>
              <Input
                id="c-phone"
                type="tel"
                autoComplete="tel"
                value={contact.phone}
                onChange={(e) => setContact({ ...contact, phone: e.target.value })}
              />
            </Field>
          </div>
        </div>

        {/* Optional, and last: a code is a bonus, not a step. Putting it above the contact
            details would make everybody without one feel they were missing something. */}
        <div>
          <Field label={t('k.promoCode')} htmlFor="c-promo" hint={t('k.promoHint')}>
            <Input
              id="c-promo"
              className="tabular uppercase"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              value={promotionCode}
              onChange={(e) => setPromotionCode(e.target.value.toUpperCase())}
            />
          </Field>
        </div>

        {error && <Alert tone="error">{error}</Alert>}

        <div>
          <Button type="submit" size="lg" block loading={stage === 'paying'}>
            {t('k.payConfirm')}
          </Button>
          <p className="mt-2 text-center text-xs" style={{ color: 'var(--color-faint)' }}>
            {seatCodes.length} seat{seatCodes.length === 1 ? '' : 's'} with {branding.appName} ·
            Payment in {settings.currency}
          </p>
          <SandboxInstruments />
        </div>
      </form>
    </section>
  );
}
